/**
 * Centralized deck display helpers.
 *
 * Every surface that renders a deck's topic/domain should use these helpers
 * instead of the old `deck.topic || 'Untitled Topic'` fallback pattern. That
 * pattern produces ugly "Untitled Topic" placeholders for two distinct
 * states that users can't distinguish:
 *
 *   1. The deck is still being processed (metadata pending).
 *   2. The deck's metadata extraction failed and needs review.
 *   3. Legacy decks uploaded before the Chunk 2 data-integrity fix that
 *      have `topic=""` persisted as bad data.
 *
 * These helpers resolve all three cases to meaningful labels and consistent
 * styling state, so downstream components can show appropriate affordances.
 *
 * Contract:
 *   - resolveDeckTopic(deck) - user-facing topic label (never empty string,
 *     never raw filename, never "Untitled Topic")
 *   - resolveDeckDomain(deck) - user-facing domain label
 *   - isDeckMetadataPending(deck) - true when pending/needs_review/missing
 *   - getDeckMetadataState(deck) - 'ready' | 'pending' | 'needs_review' |
 *     'processing' | 'missing'
 *
 * Default fallback label: "Pending Metadata" for topic, "Uncategorized" for
 * domain. These should be styled subtly (muted color + italic) via CSS so
 * users can visually tell them apart from real topics.
 */

const PENDING_TOPIC_LABEL = 'Pending Metadata';
const PENDING_DOMAIN_LABEL = 'Uncategorized';
const COMPLETED_EXAM_DECK_STATUSES = new Set(['parsed', 'completed', 'complete']);

/**
 * Detect titles that are raw filename leftovers (timestamp prefix, course
 * code prefix, etc.) — mirrors the backend is_filename_shaped() logic but
 * is more permissive so we only use it for display, not persistence.
 */
function looksLikeFilenameLeftover(str) {
  if (!str || typeof str !== 'string') return true;
  const s = str.trim();
  if (!s) return true;
  // Leading digit run (timestamp like "20260413_151406_")
  if (/^\d{6,14}[_\s]/.test(s)) return true;
  // Pure path fragment
  if (s.includes('/') || s.includes('\\')) return true;
  return false;
}

/**
 * Get the deck's metadata resolution state. This is the primary state
 * machine for display decisions — use it to pick labels AND styles.
 *
 * Returns one of:
 *   'missing'       — no deck object at all
 *   'processing'    — still parsing (parsing_status === 'parsing')
 *   'needs_review'  — pipeline ran but LLM couldn't resolve domain/topic
 *   'pending'       — uploaded but not yet parsed (heuristic refused)
 *   'ready'         — has real domain AND topic values
 */
export function getDeckMetadataState(deck) {
  if (!deck) return 'missing';

  // Explicit parsing states take precedence
  if (deck.parsing_status === 'parsing') return 'processing';

  // The backend flags decks via domain_source/topic_source
  const ds = deck.domain_source;
  const ts = deck.topic_source;
  if (ds === 'needs_review' || ts === 'needs_review') return 'needs_review';
  if (ds === 'pending' || ts === 'pending') return 'pending';

  // Field-based fallback for decks that pre-date the source-tracking fix
  const hasTopic = deck.topic && !looksLikeFilenameLeftover(deck.topic);
  const hasDomain = deck.domain && !looksLikeFilenameLeftover(deck.domain);
  if (hasTopic && hasDomain) return 'ready';
  if (deck.parsing_status === 'completed') return 'needs_review';
  return 'pending';
}

/**
 * Resolve a display-friendly topic label for a deck.
 *
 * Precedence:
 *   1. deck.topic (if non-empty and not filename-shaped)
 *   2. deck.title (if non-empty, not filename-shaped, and looks distinct
 *      from a filename — e.g. the LLM-extracted title from slide 1)
 *   3. options.processingLabel when deck is still parsing
 *   4. Pending label (never empty string, never "Untitled Topic")
 *
 * The old pattern was `deck.topic || 'Untitled Topic'` which didn't
 * distinguish state and hid failing decks from admin attention.
 */
export function resolveDeckTopic(deck, options = {}) {
  if (!deck) return options.fallback || PENDING_TOPIC_LABEL;

  if (deck.topic && !looksLikeFilenameLeftover(deck.topic)) {
    return deck.topic;
  }

  if (deck.title && !looksLikeFilenameLeftover(deck.title)) {
    return deck.title;
  }

  const state = getDeckMetadataState(deck);
  if (state === 'processing' && options.processingLabel) {
    return options.processingLabel;
  }

  return options.fallback || PENDING_TOPIC_LABEL;
}

/**
 * Resolve a display-friendly domain label for a deck.
 */
export function resolveDeckDomain(deck, options = {}) {
  if (!deck) return options.fallback || PENDING_DOMAIN_LABEL;

  if (deck.domain && !looksLikeFilenameLeftover(deck.domain)) {
    return deck.domain;
  }

  return options.fallback || PENDING_DOMAIN_LABEL;
}

/**
 * True when the deck has not yet resolved to a real topic+domain.
 * Callers can use this to apply muted styling, show a spinner, etc.
 */
export function isDeckMetadataPending(deck) {
  const state = getDeckMetadataState(deck);
  return state !== 'ready';
}

/**
 * True when the deck has an empty/filename-shaped topic AND no meaningful
 * title — i.e. there's nothing user-friendly to render at all. Useful for
 * filtering ghost decks out of map navbar groupings.
 */
export function isDeckDisplayUnresolvable(deck) {
  if (!deck) return true;
  const hasTopic = deck.topic && !looksLikeFilenameLeftover(deck.topic);
  const hasTitle = deck.title && !looksLikeFilenameLeftover(deck.title);
  return !hasTopic && !hasTitle;
}

/**
 * Validate an exam-progress deck entry before rendering it in the Dashboard
 * tab strip. This mirrors the backend contract:
 *   - every rendered tab must have a real deck_id and non-empty deck_name
 *   - completed decks must carry at least one subtopic
 *   - processing / failed decks may omit subtopics while they are in-flight
 */
export function validateExamDeckProgress(deck) {
  const deckId = typeof deck?.deck_id === 'string' ? deck.deck_id.trim() : '';
  const deckName = typeof deck?.deck_name === 'string' ? deck.deck_name.trim() : '';
  const parsingStatus = typeof deck?.parsing_status === 'string' ? deck.parsing_status.trim() : '';
  const isPendingDeck = parsingStatus && !COMPLETED_EXAM_DECK_STATUSES.has(parsingStatus);
  const subtopics = Array.isArray(deck?.subtopics) ? deck.subtopics : [];

  if (!deckId) {
    return { renderable: false, reason: 'missing_deck_id', deckId, deckName, isPendingDeck };
  }

  if (!deckName) {
    return { renderable: false, reason: 'missing_deck_name', deckId, deckName, isPendingDeck };
  }

  if (!isPendingDeck && subtopics.length === 0) {
    return { renderable: false, reason: 'completed_without_subtopics', deckId, deckName, isPendingDeck };
  }

  return { renderable: true, reason: null, deckId, deckName, isPendingDeck };
}

// CSS class name helpers — downstream components can apply these directly
// to render a consistent "pending metadata" visual treatment.
export function getDeckTopicClassName(deck, baseClass = '') {
  const state = getDeckMetadataState(deck);
  const stateClass =
    state === 'ready' ? '' : `deck-topic--${state.replace('_', '-')}`;
  return `${baseClass} ${stateClass}`.trim();
}

// Exposed for tests and edge cases where a caller needs the raw check.
export const _looksLikeFilenameLeftover = looksLikeFilenameLeftover;
